import {
  Component,
  ChangeDetectionStrategy,
  inject,
  OnInit,
  DestroyRef,
  signal,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SidebarService } from '../../../core/services/sidebar.service';
import { LayoutService } from '../../../core/services/layout.service';
import { PermissaoService } from '../../../core/services/permissao.service';
import { ProfileService } from '../../../core/services/profile.service';
import { AuthService } from '../../../core/services/auth.service';
import { ModalComponent } from '../../components/design-system/modal.component';
import { ButtonComponent } from '../../components/design-system/button.component';
import { BadgeComponent } from '../../components/design-system/badge.component';
import { environment } from '../../../../environments/environment';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { version } from '../../../../../package.json';
import { filter, map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarComponent,
    ModalComponent,
    ButtonComponent,
    BadgeComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements OnInit {
  private sidebarService = inject(SidebarService);
  private permissaoService = inject(PermissaoService);
  private router = inject(Router);
  private location = inject(Location);
  readonly layoutService = inject(LayoutService);
  private destroyRef = inject(DestroyRef);
  readonly profileService = inject(ProfileService);
  readonly authService = inject(AuthService);

  readonly usuarioCompleto = this.profileService.currentUser;
  readonly appVersion = 'v' + version;
  readonly showProfileModal = signal(false);
  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  ngOnInit(): void {
    if (!this.permissaoService.carregado()) {
      this.permissaoService
        .carregarPermissoes(environment.aplicacaoId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  get isMobileOrTablet() {
    return this.layoutService.isMobile() || this.layoutService.isTablet();
  }

  get isSidebarExpanded() {
    return this.isMobileOrTablet
      ? this.sidebarService.getIsOpenMobile()()
      : this.sidebarService.isExpanded();
  }

  toggleSidebar() {
    if (this.isMobileOrTablet) {
      this.sidebarService.toggleMobile();
    } else {
      this.sidebarService.toggle();
    }
  }

  closeSidebar() {
    if (this.isMobileOrTablet) {
      this.sidebarService.closeMobile();
    }
  }

  showMobileBackButton(): boolean {
    if (!this.isMobileOrTablet) {
      return false;
    }
    const url = this.currentUrl();
    return url.startsWith('/aplicacoes/') && url !== '/aplicacoes';
  }

  goBackFromMobileHeader(): void {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this.location.back();
      return;
    }
    this.router.navigate(['/aplicacoes']);
  }

  logout() {
    this.showProfileModal.set(false);
    this.authService.logout();
  }
}
